import type {
  IngridCompleteSessionResponse,
  Option,
  OrderDTO,
  PaymentProviderConfig,
  QliroOrder,
  ShippingProviderConfig,
  ShippingProviderName
} from 'ecosystem'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { qliroApi } from '../../../utils/qliro'
import { createError } from '../../../utils/fetching'
import { alertService } from '../../../utils/alert-service'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import type { OrderProvisionResult, ProvisionOrderOptions } from './types'
import {
  generateIngridShippingProviderResult,
  generateNshiftShippingProviderResult,
  generatePostnordShippingProviderResult,
  generateQliroAddresses,
  isOrderAlreadyProvisioned,
  isOrderFresh
} from './utils'

export interface ProvisionOrderQliroProviderResult {
  qliroResponse: QliroOrder
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: {
    providerName: ShippingProviderName
    ingridResponse?: IngridCompleteSessionResponse['session']
  }
}

export const provisionOrderQliroProvider = async (
  storefrontOrderNumber: string, // storefront order number
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig,
  options?: ProvisionOrderOptions
): Promise<Option<ProvisionOrderQliroProviderResult>> => {
  // 1. Get storefront order
  const { error: getOrderError, data: orderData } = await getStorefrontOrderByOrderNumber({
    orderNumber: storefrontOrderNumber
  })

  if (getOrderError || !orderData) {
    void alertService.send('Provision order | Get storefront order', {
      blocks: [
        {
          title: 'Payload',
          content: { storefrontOrderNumber }
        },
        {
          title: 'Error',
          content: getOrderError
        }
      ]
    })

    throw createError(getOrderError ?? 'BE GET endpoint error')
  }

  // 2. Get the order from Qliro
  const { error: qliroError, data: qliroOrderResponse } =
    await qliroApi.checkout.retrieveOrderByReference(storefrontOrderNumber)

  if (qliroError || !qliroOrderResponse) {
    void alertService.send('Provision order | Get qliro order', {
      blocks: [
        {
          title: 'Payload',
          content: { merchantReference: storefrontOrderNumber }
        },
        {
          title: 'Error',
          content: qliroError
        }
      ]
    })

    throw Error(qliroError || 'Qliro GET endpoint error')
  }

  // 3. Validate order status
  if (qliroOrderResponse.CustomerCheckoutStatus !== 'Completed') {
    void alertService.send('Provision order | Qliro order not completed', {
      blocks: [
        {
          title: 'Qliro Order Response',
          content: qliroOrderResponse
        },
        {
          title: 'Error',
          content: `Qliro order status is ${qliroOrderResponse.CustomerCheckoutStatus}, expected 'Completed'`
        }
      ]
    })

    throw Error('Qliro order is not completed yet')
  }

  // Idempotency guard: the order is provisioned exactly once. If it is no longer CREATED, another
  // path (confirmation page, or an earlier push retry) already provisioned it — return the existing
  // order without re-booking shipment or re-writing it, to avoid duplicates.
  if (isOrderAlreadyProvisioned(orderData)) {
    return { storefrontOrder: orderData, qliroResponse: qliroOrderResponse }
  }

  // Fallback gate (payment push only): a still-fresh order belongs to the confirmation page; throw
  // so the push endpoint responds non-2xx and the provider retries later. This keeps the page the
  // single provisioner during the post-checkout window, eliminating the page-vs-push race.
  if (options?.asFallback && isOrderFresh(orderData)) {
    throw createError(
      'Payment push received for a still-fresh order; deferring to the confirmation page'
    )
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData?.shippingProvider) {
      return
    }

    switch (orderData?.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        const { customerInfo } = generateQliroAddresses(qliroOrderResponse)

        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: customerInfo.phone,
            smsNo: customerInfo.phone,
            emailAddress: customerInfo.email,
            contactName: customerInfo.fullName
          },
          shippingProviderConfig
        )
      }
      case 'NSHIFT': {
        const { customerInfo } = generateQliroAddresses(qliroOrderResponse)

        return generateNshiftShippingProviderResult(orderData, {
          phone: customerInfo.phone,
          email: customerInfo.email,
          name: customerInfo.fullName
        })
      }
      case 'DEFAULT':
    }
  }

  // 4. Get shipping provider information
  const shippingProviderResult = await getShippingProviderResult()

  // 5. Complete storefront order
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'QLIRO',
      providerResponse: {
        qliroResponse: qliroOrderResponse
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  return {
    storefrontOrder,
    qliroResponse: qliroOrderResponse,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
